import { useState, useEffect } from "react";
import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pencil, Trash2, Plus, Eye, EyeOff, ArrowLeft } from "lucide-react";

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  content: string;
  excerpt: string | null;
  coverImage: string | null;
  metaTitle: string | null;
  metaDescription: string | null;
  ogImage: string | null;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

function slugify(text: string) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export default function BlogManager() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [editing, setEditing] = useState<BlogPost | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [content, setContent] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [ogImage, setOgImage] = useState("");
  const [published, setPublished] = useState(false);
  const [autoSlug, setAutoSlug] = useState(true);
  const [showSeo, setShowSeo] = useState(false);

  useEffect(() => {
    loadPosts();
  }, []);

  async function loadPosts() {
    try {
      const data = await apiFetch("/blog/posts/all");
      setPosts(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function startNew() {
    setEditing(null);
    setIsNew(true);
    setTitle("");
    setSlug("");
    setContent("");
    setExcerpt("");
    setCoverImage("");
    setMetaTitle("");
    setMetaDescription("");
    setOgImage("");
    setPublished(false);
    setAutoSlug(true);
    setShowSeo(false);
    setError("");
  }

  function startEdit(post: BlogPost) {
    setEditing(post);
    setIsNew(false);
    setTitle(post.title);
    setSlug(post.slug);
    setContent(post.content);
    setExcerpt(post.excerpt || "");
    setCoverImage(post.coverImage || "");
    setMetaTitle(post.metaTitle || "");
    setMetaDescription(post.metaDescription || "");
    setOgImage(post.ogImage || "");
    setPublished(post.published);
    setAutoSlug(false);
    setShowSeo(!!(post.metaTitle || post.metaDescription || post.ogImage));
    setError("");
  }

  function cancelEdit() {
    setEditing(null);
    setIsNew(false);
    setError("");
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const body = { title, slug, content, excerpt: excerpt || null, coverImage: coverImage || null, metaTitle: metaTitle || null, metaDescription: metaDescription || null, ogImage: ogImage || null, published };
      if (isNew) {
        await apiFetch("/blog/posts", { method: "POST", body: JSON.stringify(body) });
      } else if (editing) {
        await apiFetch(`/blog/posts/${editing.id}`, { method: "PUT", body: JSON.stringify(body) });
      }
      await loadPosts();
      cancelEdit();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: number) {
    if (!confirm("Are you sure you want to delete this post?")) return;
    try {
      await apiFetch(`/blog/posts/${id}`, { method: "DELETE" });
      await loadPosts();
    } catch (err: any) {
      setError(err.message);
    }
  }

  async function togglePublish(post: BlogPost) {
    try {
      await apiFetch(`/blog/posts/${post.id}`, {
        method: "PUT",
        body: JSON.stringify({ published: !post.published }),
      });
      await loadPosts();
    } catch (err: any) {
      setError(err.message);
    }
  }

  if (isNew || editing) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <button onClick={cancelEdit} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to posts
        </button>
        <h2 className="text-xl font-serif font-bold mb-4">{isNew ? "New Blog Post" : "Edit Post"}</h2>

        {error && <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-md">{error}</div>}

        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (autoSlug) setSlug(slugify(e.target.value));
              }}
              required
            />
          </div>
          <div>
            <Label htmlFor="slug">URL Slug</Label>
            <Input
              id="slug"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setAutoSlug(false);
              }}
              required
            />
          </div>
          <div>
            <Label htmlFor="excerpt">Excerpt (optional)</Label>
            <Input id="excerpt" value={excerpt} onChange={(e) => setExcerpt(e.target.value)} placeholder="Brief summary for the blog listing" />
          </div>
          <div>
            <Label htmlFor="coverImage">Cover Image URL (optional)</Label>
            <Input id="coverImage" value={coverImage} onChange={(e) => setCoverImage(e.target.value)} placeholder="Paste an image URL or upload via gallery" />
          </div>
          <div>
            <Label htmlFor="content">Content</Label>
            <textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full min-h-[300px] border border-input bg-background px-3 py-2 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="Write your blog post content here. HTML is supported."
            />
          </div>
          <div className="border-t pt-4 mt-4">
            <button
              type="button"
              onClick={() => setShowSeo(!showSeo)}
              className="text-sm font-medium text-primary hover:text-primary/80 flex items-center gap-1"
            >
              {showSeo ? "▾" : "▸"} SEO Settings (optional)
            </button>
            {showSeo && (
              <div className="mt-3 space-y-3 bg-muted/30 p-4 rounded-lg">
                <p className="text-xs text-muted-foreground">
                  Customize how this post appears in Google results and when shared on social media. If left blank, the post title and excerpt will be used automatically.
                </p>
                <div>
                  <Label htmlFor="metaTitle">Search Title</Label>
                  <Input
                    id="metaTitle"
                    value={metaTitle}
                    onChange={(e) => setMetaTitle(e.target.value)}
                    placeholder={title ? `${title} - Tallahassee Lassie Pet Care` : "Auto-generated from post title"}
                  />
                  <p className="text-xs text-muted-foreground mt-1">What people see in Google search results</p>
                </div>
                <div>
                  <Label htmlFor="metaDescription">Search Description</Label>
                  <textarea
                    id="metaDescription"
                    value={metaDescription}
                    onChange={(e) => setMetaDescription(e.target.value)}
                    placeholder={excerpt || "Auto-generated from excerpt"}
                    className="w-full min-h-[80px] border border-input bg-background px-3 py-2 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <p className="text-xs text-muted-foreground mt-1">The snippet shown below the title in Google results (recommended: 150-160 characters)</p>
                </div>
                <div>
                  <Label htmlFor="ogImage">Social Share Image URL</Label>
                  <Input
                    id="ogImage"
                    value={ogImage}
                    onChange={(e) => setOgImage(e.target.value)}
                    placeholder={coverImage || "Uses cover image if not set"}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Image shown when this post is shared on Facebook, LinkedIn, etc.</p>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input type="checkbox" id="published" checked={published} onChange={(e) => setPublished(e.target.checked)} />
            <Label htmlFor="published" className="mb-0">Publish immediately</Label>
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={saving} style={{ borderRadius: "16px 0 16px 0" }}>
              {saving ? "Saving..." : isNew ? "Create Post" : "Save Changes"}
            </Button>
            <Button type="button" variant="outline" onClick={cancelEdit}>Cancel</Button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-serif font-bold">Blog Posts</h2>
        <Button onClick={startNew} style={{ borderRadius: "16px 0 16px 0" }}>
          <Plus className="w-4 h-4 mr-1" /> New Post
        </Button>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-md">{error}</div>}

      {loading ? (
        <p className="text-muted-foreground">Loading posts...</p>
      ) : posts.length === 0 ? (
        <div className="bg-white p-12 text-center rounded-lg shadow-sm">
          <p className="text-muted-foreground mb-4">No blog posts yet. Create your first one!</p>
          <Button onClick={startNew} style={{ borderRadius: "16px 0 16px 0" }}>
            <Plus className="w-4 h-4 mr-1" /> New Post
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <div key={post.id} className="bg-white p-4 rounded-lg shadow-sm flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold">{post.title}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${post.published ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                    {post.published ? "Published" : "Draft"}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  /{post.slug} · {new Date(post.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => togglePublish(post)} className="p-2 hover:bg-gray-100 rounded-md" title={post.published ? "Unpublish" : "Publish"}>
                  {post.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button onClick={() => startEdit(post)} className="p-2 hover:bg-gray-100 rounded-md" title="Edit">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(post.id)} className="p-2 hover:bg-red-50 text-red-500 rounded-md" title="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
